import React, { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';

import styled from 'styled-components';
import AccordionPanel from '@components/common/app-components/AccordionPanel';

import { useAppDispatch, useAppSelector, useModal, useExchangeConditions } from '@hooks/index';
import EnrolledMembersWrapper from '../EnrolledMembers/EnrolledMembersWrapper';
import { ConditionalRendering } from '@components/common/conditional-rendering';
import { PlAN_TYPES, GROUP_LABEL_IDS } from '@constants/individualPortal/customGrouping/customGrouping.constants';
import UnenrolledMembersWrapper from '../UnenrolledMembers/UnenrolledMembersWrapper';
import GroupedMembersWrapper from '../GroupedMembers/GroupedMembersWrapper';
import { environmentSelector } from '@selectors/commonSelectors';
import { APP_TYPES } from '@constants/individualPortal/dashboard/dashboardModelTypes';
import useCustomGrouping from '../../hooks/useCustomGrouping';

const StyledAccordionContainer = styled.div`
    & > * {
        border: 0px;
    }
    .header-container {
        background-color: ${({ theme }) => theme.gray10};
        border-radius: 0px;
        padding: 0px 1%;
        letter-spacing: 1px;
    }

    .content {
        border-top: 0px;
        background-color: white;
    }

    .toggle-icon {
        text-decoration: none;
        color: ${({ theme }) => theme.primaryDark};
    }
`;

const StyledAccordionContainerWithBorder = styled.div`
    & > * {
        border: ${({ theme }) => `1px solid ${theme.gray8}`};
    }

    .header-container {
        background-color: ${({ theme }) => theme.gray10};
        border-radius: 0px;
        padding: 0px 1%;
        letter-spacing: 1px;
    }

    .content {
        border-top: 0px;
    }
`;

const DentalPlan = props => {
    const { setActiveTab, daysRemaining, index } = props;

    const customGroupingState = useAppSelector(state => state.individualPortal.customGrouping);
    const dispatch = useAppDispatch();
    const {
        isHouseHoldAPTCEligible,
        areAllMembersEnrolledInGroup,
        isAllEnrolledInHealth,
        updateDentalActivePassiveRenewalGroups,
        areAllMembersAddedToGroups,
    } = useCustomGrouping({ dispatch });

    const {
        isAnyDentalGroupEnrolled = false,
        isDentalRenewalGroupAvailable = false,
        DentalGroupEnrolledMembersCount = 0,
        dentalGroups = [],
        unEnrolledDentalGroup = [],
        hasAIMembers = false,
        addDentalMembersGroup = [],
        applicationType,
        groupList = [],
        isInsideOEEnrollmentWindow,
        isRenewalApplication,
        isHealthRenewalGroupAvailable,
        isIneligibleMembersPresent,
        isDentalRenewed,
        isAllMembersEnrolledInDental,
    } = customGroupingState;

    const { openModal } = useModal();
    const { isApplicationNotToConsiderForDentalVisible, isAIANMemberAlertVisible, isAddMemberWarningVisible, isBoldHeader } =
        useExchangeConditions();
    const environment = useAppSelector(environmentSelector);

    useEffect(() => {
        if (environment.flags.allowDentalWithoutHealthEnrollment) {
            if (
                [APP_TYPES.QEP, APP_TYPES.OE].includes(applicationType) &&
                isHouseHoldAPTCEligible(groupList) &&
                !areAllMembersEnrolledInGroup(groupList)
            ) {
                openModal('healthBeforeDentalModal', { setActiveTab });
            }
        } else {
            const hasDentalGroups = dentalGroups.length > 0;
            const allEnrolledInHealth = hasDentalGroups && isAllEnrolledInHealth(dentalGroups[0]);

            if (hasDentalGroups && !allEnrolledInHealth && !isAllMembersEnrolledInDental) {
                openModal('dentalEnrollmentRequirement', { setActiveTab });
            } else if (allEnrolledInHealth) {
                setActiveTab(1);
            }
            if (hasDentalGroups && isInsideOEEnrollmentWindow && applicationType === APP_TYPES.OE) {
                updateDentalActivePassiveRenewalGroups({
                    isRenewalApplication,
                    isHealthRenewalGroupAvailable,
                    isIneligibleMembersPresent,
                    isDentalRenewalGroupAvailable,
                    isDentalRenewed,
                    groupList,
                    dentalGroups,
                });
            }
        }
    }, []);

    const AccordionHeader = () => {
        return (
            <p className={`${isBoldHeader ? 'bold-header' : ''}`} data-testid="accordion-header">
                <FormattedMessage
                    id={
                        isDentalRenewalGroupAvailable
                            ? 'customGrouping.coverageYearMemberEnrolled'
                            : 'customGrouping.enrolledMember'
                    }
                    values={{ memberCount: DentalGroupEnrolledMembersCount }}
                />
            </p>
        );
    };

    function areAllMembersAddedToDentalGroup() {
        return unEnrolledDentalGroup.every(group => {
            return areAllMembersAddedToGroups(group);
        });
    }

    return (
        <div id={`tabPanel-${index}`} role="tabpanel" aria-labelledby={`tab-${index}`}>
            <ConditionalRendering isTrue={isAnyDentalGroupEnrolled}>
                <ConditionalRendering isTrue={isDentalRenewalGroupAvailable}>
                    <ConditionalRendering isTrue={!isApplicationNotToConsiderForDentalVisible}>
                        <FormattedMessage
                            id="customGrouping.dentalChangeGroup1"
                            data-testid="dental-change-group1"
                        />{' '}
                        <FormattedMessage
                            id="customGrouping.dentalChangeGroup2"
                            data-testid="dental-change-group2"
                        />
                        <br />
                        <FormattedMessage
                            id="customGrouping.dentalRenewalMessage"
                            data-testid="dental-renewal-message"
                        />
                    </ConditionalRendering>
                    <ConditionalRendering isTrue={isApplicationNotToConsiderForDentalVisible}>
                        <p data-testid="renew-change-group1">
                            <FormattedMessage id="customGrouping.renewChangeGroup1" />{' '}
                            <a
                                href="#"
                                onClick={e => {
                                    e.preventDefault();
                                    openModal('changeApplicationNotToConsider', { typeOfPlan: PlAN_TYPES.DENTAL });
                                }}
                                data-testid="change-application-link"
                            >
                                <FormattedMessage
                                    id="customGrouping.renewChangeGroup2"
                                    data-testid="renew-change-group2"
                                />
                            </a>
                        </p>
                    </ConditionalRendering>
                </ConditionalRendering>
                <StyledAccordionContainer data-testid="styled-accordion-container">
                    <AccordionPanel
                        id="dentalAccordian"
                        ariaControlsId='dentalAccordianPanel'
                        isExpandable={true}
                        header={<AccordionHeader data-testid="accordion-header" />}
                        isInitialExpanded={
                            environment?.enableDentalCustomGrouping && addDentalMembersGroup.length === 0
                        }
                        data-testid="accordion-panel-enrolled"
                    >
                        <EnrolledMembersWrapper
                            {...{ enrolledGroups: dentalGroups }}
                            planType={PlAN_TYPES.DENTAL}
                            data-testid="enrolled-members-wrapper"
                        />
                    </AccordionPanel>
                </StyledAccordionContainer>
            </ConditionalRendering>
            <ConditionalRendering isTrue={unEnrolledDentalGroup.length && !areAllMembersAddedToDentalGroup()}>
                <>
                    <p data-testid="whom-you-are-shopping-dental" className={`${isBoldHeader ? 'bold-header' : ''} mt-4`}>
                        <FormattedMessage
                            id="customGrouping.whomYouAreShoppingDental"
                            data-testid="whom-you-are-shopping-dental-message"
                        />
                    </p>
                    <ConditionalRendering isTrue={isAIANMemberAlertVisible}>
                        <p data-testid="aian-member-alert">
                            <FormattedMessage
                                id={
                                    hasAIMembers
                                        ? 'customGrouping.aiAnMember1'
                                        : 'customGrouping.aiAnMember2'
                                }
                                data-testid="aian-member-message"
                            />
                        </p>
                    </ConditionalRendering>
                    <ConditionalRendering isTrue={!isAIANMemberAlertVisible}>
                        <p data-testid="aian-member2">
                            <FormattedMessage id="customGrouping.aiAnMember2" />
                        </p>
                        <p data-testid="aian-member3">
                            <FormattedMessage id="customGrouping.aiAnMember3" />
                        </p>
                        <p data-testid="aian-member4">
                            <FormattedMessage id="customGrouping.aiAnMember4" />
                        </p>
                        <p data-testid="aian-member5">
                            <FormattedMessage id="customGrouping.aiAnMember5" />
                        </p>
                    </ConditionalRendering>

                    <p id={GROUP_LABEL_IDS.SHOP_FOR_THESE_DENTAL} data-testid="shop-for-these" className={`${isBoldHeader ? 'bold-header' : ''}`}>
                        <FormattedMessage
                            id="customGrouping.shopForThese"
                            data-testid="shop-for-these-message"
                        />
                    </p>

                    <ConditionalRendering isTrue={!!dentalGroups.length}>
                        <UnenrolledMembersWrapper
                            unenrolledGroups={unEnrolledDentalGroup}
                            planType={PlAN_TYPES.DENTAL}
                            groupLabelId={GROUP_LABEL_IDS.SHOP_FOR_THESE_DENTAL}
                            data-testid="unenrolled-members-wrapper"
                        />
                    </ConditionalRendering>
                </>
            </ConditionalRendering>

            <ConditionalRendering isTrue={!!addDentalMembersGroup.length && environment?.enableDentalCustomGrouping}>
                <StyledAccordionContainerWithBorder
                    className="mt-4"
                    data-testid="styled-accordion-container-with-border"
                >
                    <AccordionPanel
                        id="dentalAccordianUnenrolled"
                        ariaControlsId='dentalAccordianUnenrolledPanel'
                        isExpandable={false}
                        header={
                            <p className={`${isBoldHeader ? 'bold-header' : ''}`}>
                                <FormattedMessage
                                    id="customGrouping.selectOption"
                                    data-testid="select-option"
                                />
                            </p>
                        }
                        isInitialExpanded={true}
                        data-testid="accordion-panel-unenrolled"
                    >
                        <ConditionalRendering isTrue={isAddMemberWarningVisible}>
                            <p data-testid="add-members-warning">
                                <FormattedMessage id="customGrouping.addMembers.warning1" />
                            </p>
                            <p>
                                <FormattedMessage
                                    id="customGrouping.addMembers.warning2"
                                    values={{ daysRemaining }}
                                    data-testid="add-members-warning2"
                                />
                            </p>
                        </ConditionalRendering>
                        <GroupedMembersWrapper
                            groups={addDentalMembersGroup}
                            planType={PlAN_TYPES.DENTAL}
                            data-testid="grouped-members-wrapper"
                        />
                    </AccordionPanel>
                </StyledAccordionContainerWithBorder>
            </ConditionalRendering>

            <ConditionalRendering isTrue={!dentalGroups.length}>
                <p className="alert alert-info" data-testid="no-dental-group">
                    <FormattedMessage id="customGrouping.noDentalGroup" />
                </p>
            </ConditionalRendering>
        </div>
    );
};

export default DentalPlan;
